import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { MiBox, MiDetailFields, MiList, MiModal } from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_TYPES,
  ENTITY_TYPE_LABELS,
  FILE_MIME_TYPE_EXTENSIONS,
  FONT_WEIGHTS,
  INPUT_TYPES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
  VALID_FILE_OPTIONS,
} from "@miplatform/constants";
import { IDocumentCategoriesResponse } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiPencilOutline, mdiTrashCanOutline } from "@mdi/js";
import { AddButton } from "@mibuildweb/components";
import { useDocumentCategories, usePermissions } from "@mibuildweb/hooks";
import { Typography, useTheme } from "@mui/material";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigCard } from "../components";

const attachedToOptions = [
  { value: ENTITY_TYPES.HOME, label: ENTITY_TYPE_LABELS[ENTITY_TYPES.HOME] },
  { value: ENTITY_TYPES.ORDER, label: ENTITY_TYPE_LABELS[ENTITY_TYPES.ORDER] },
  {
    value: ENTITY_TYPES.INSPECTION,
    label: ENTITY_TYPE_LABELS[ENTITY_TYPES.INSPECTION],
  },
  {
    value: ENTITY_TYPES.INVOICE,
    label: ENTITY_TYPE_LABELS[ENTITY_TYPES.INVOICE],
  },
  {
    value: ENTITY_TYPES.STEP,
    label: ENTITY_TYPE_LABELS[ENTITY_TYPES.STEP],
  },
];

export function ConfigDocumentTypesList() {
  const theme = useTheme();
  const [selectedDocumentCategory, setSelectedDocumentCategory] =
    useState<IDocumentCategoriesResponse | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openCreateEditModal, setOpenCreateEditModal] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const { hasPermission } = usePermissions();
  const canCreateDocumentType = hasPermission(
    PERMISSIONS.DOCUMENT_CATEGORY.CREATE
  );
  const canEditDocumentType = hasPermission(
    PERMISSIONS.DOCUMENT_CATEGORY.UPDATE
  );
  const canDeleteDocumentType = hasPermission(
    PERMISSIONS.DOCUMENT_CATEGORY.DELETE
  );

  const defaultDocumentCategoryData: IDocumentCategoryFormData = {
    name: "",
    dependentType: "",
    allowedFileFormats: [],
  };
  const {
    control,
    formState: { isValid, isDirty },
    reset,
    getValues,
  } = useForm<IDocumentCategoryFormData>({
    mode: "onChange",
    defaultValues: defaultDocumentCategoryData,
    resolver: zodResolver(schema),
  });

  const {
    getDocumentCategories,
    setDocumentCategories,
    documentCategories,
    setRecentDocumentCategories,
    recentDocumentCategories,
    updateDocumentCategory,
    deleteDocumentCategory,
    createDocumentCategory,
    search,
    setSearch,
    debouncedSearch,
    isLoading,
  } = useDocumentCategories();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const documentTypeFieldset = [
    {
      formControl: control,
      label: "Name",
      name: "name",
      width: "100%",
      required: true,
    },
    {
      formControl: control,
      label: "Allowed File Formats",
      name: "allowedFileFormats",
      width: "50%",
      required: true,
      fieldType: INPUT_TYPES.MULTI_SELECT,
      options: VALID_FILE_OPTIONS,
    },
    {
      formControl: control,
      label: "Attached To",
      name: "dependentType",
      width: "50%",
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: attachedToOptions,
    },
  ];

  const handleOpenDeleteModal = (item: IDocumentCategoriesResponse) => {
    setSelectedDocumentCategory(item);
    reset({ ...item });
    setOpenDeleteModal(true);
  };

  const handleOpenEditModal = (item: IDocumentCategoriesResponse) => {
    setSelectedDocumentCategory(item);
    reset({ ...item });
    setOpenCreateEditModal(true);
  };

  const handleCloseModal = () => {
    setOpenCreateEditModal(false);
    setOpenDeleteModal(false);
    reset(defaultDocumentCategoryData);
    setSelectedDocumentCategory(null);
  };

  const handleDelete = async () => {
    if (selectedDocumentCategory) {
      await deleteDocumentCategory({
        id: selectedDocumentCategory.id,
        modifiedOn: selectedDocumentCategory.modifiedOn,
      });
    }
    handleCloseModal();
    refreshData();
    setSelectedDocumentCategory(null);
  };

  const handleSubmit = async () => {
    if (selectedDocumentCategory) {
      const updatedData = { ...selectedDocumentCategory, ...getValues() };
      await updateDocumentCategory({ data: updatedData });
      setOpenCreateEditModal(false);
    } else {
      await createDocumentCategory({ data: getValues() });
    }

    refreshData();
    handleCloseModal();
    setSelectedDocumentCategory(null);
    reset();
  };

  const refreshData = () => {
    setTriggerRefresh((prev) => !prev);
  };

  const refreshDocumentTypes = useMemo(() => {
    return { triggerRefresh };
  }, [triggerRefresh]);

  const modalTitle = selectedDocumentCategory
    ? `Edit Document Type: ${selectedDocumentCategory?.name}`
    : "Create New Document Type";

  const modalDescription = selectedDocumentCategory
    ? "Edit the document type details below."
    : "Add a document type to your org.";

  const modalSubmitText = selectedDocumentCategory
    ? "Save Changes"
    : "Add New Document Type";

  const deleteModalActions = [
    {
      variant: BUTTON_VARIANTS.TEXT,
      title: BUTTON_LABELS.CANCEL,
      onClick: handleCloseModal,
    },
    {
      variant: BUTTON_VARIANTS.CONTAINED,
      color: theme.palette.red.main,
      title: BUTTON_LABELS.DELETE,
      onClick: handleDelete,
    },
  ];

  const addTypeModalActions = [
    {
      variant: BUTTON_VARIANTS.TEXT,
      title: BUTTON_LABELS.CANCEL,
      onClick: handleCloseModal,
    },
    {
      variant: BUTTON_VARIANTS.CONTAINED,
      title: modalSubmitText,
      onClick: handleSubmit,
      disabled: !isValid || !isDirty,
    },
  ];

  return (
    <ConfigPageLayout
      title="Document Types"
      search={search}
      onHandleSearch={handleSearchChange}
      hideOnBack
      loading={isLoading}
      titleActionComponent={
        canCreateDocumentType && (
          <AddButton
            onClick={() => setOpenCreateEditModal(true)}
            label="Add New Document Type"
          />
        )
      }
    >
      <MiBox>
        <MiList
          viewType="card"
          dataProvider={getDocumentCategories}
          loading={isLoading}
          data={documentCategories ?? []}
          setData={setDocumentCategories}
          CardComponent={ConfigDocumentTypeCard}
          cardProps={{
            handleOpenEditModal,
            handleOpenDeleteModal,
            canEditDocumentType,
            canDeleteDocumentType,
            onClick: handleOpenEditModal,
          }}
          paginationMode={PAGINATION.SERVER}
          search={debouncedSearch}
          refresh={refreshDocumentTypes}
          setRecentData={setRecentDocumentCategories}
          recentData={recentDocumentCategories}
          recentDataProvider={getDocumentCategories}
          emptyListProps={{
            text: "No Document Types found",
          }}
        />
      </MiBox>
      <MiModal
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        description={`You are about to delete ${selectedDocumentCategory?.name}. Are you sure you would like to continue?`}
        actions={deleteModalActions}
        closeModal={handleCloseModal}
      />
      <MiModal
        title={modalTitle}
        description={modalDescription}
        open={openCreateEditModal}
        closeModal={handleCloseModal}
        actions={addTypeModalActions}
      >
        <MiDetailFields detailFields={documentTypeFieldset} control={control} />
      </MiModal>
    </ConfigPageLayout>
  );
}

const ConfigDocumentTypeCard = ({
  item,
  handleOpenDeleteModal,
  handleOpenEditModal,
  canEditDocumentType,
  canDeleteDocumentType,
  onClick,
}: {
  item: IDocumentCategoryFormData;
  handleOpenDeleteModal: (item: IDocumentCategoryFormData) => void;
  handleOpenEditModal: (item: IDocumentCategoryFormData) => void;
  canEditDocumentType: boolean;
  canDeleteDocumentType: boolean;
  onClick?: (item: IDocumentCategoryFormData) => void;
}) => {
  const { name, modifiedOn, createdOn, allowedFileFormats } = item;
  const theme = useTheme();

  const fileFormats = allowedFileFormats
    ?.map((format) => FILE_MIME_TYPE_EXTENSIONS[format])
    ?.join(", ")
    .toUpperCase();

  const title = (
    <Typography variant="BD2" fontWeight={FONT_WEIGHTS.BOLD}>
      {name}{" "}
      <Typography
        variant="BD2"
        fontWeight={FONT_WEIGHTS.BOLD}
        color={theme.palette.primary.medium}
        component="span"
      >
        {fileFormats}
      </Typography>
    </Typography>
  );

  const actions = [];
  if (canEditDocumentType && !item.isSystemDefined) {
    actions.push({
      label: BUTTON_LABELS.EDIT,
      action: () => handleOpenEditModal(item),
      icon: mdiPencilOutline,
    });
  }
  if (canDeleteDocumentType && !item.isSystemDefined) {
    actions.push({
      label: BUTTON_LABELS.DELETE,
      action: () => handleOpenDeleteModal(item),
      icon: mdiTrashCanOutline,
      color: theme.palette.red.main,
    });
  }

  return (
    <ConfigCard
      title={title}
      modifiedOn={modifiedOn}
      createdOn={createdOn}
      menuActions={actions}
      onClick={() => onClick?.(item)}
    />
  );
};

export const schema = z.object({
  name: z.string().min(1, "Name is required"),
  allowedFileFormats: z.array(z.string()).min(1, "File Types is required"),
  dependentType: z.string().min(1, "Attached To is required"),
});

interface IDocumentCategoryFormData {
  id?: string;
  createdOn?: string;
  isSystemDefined?: boolean;
  modifiedOn?: string;
  name: string;
  allowedFileFormats: string[];
  dependentType: string;
}
